import type { ChangeEvent, ReactNode } from 'react';
import type { Changes } from '../api/types';

export function Panel({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="panel">
      <header className="panel-head">
        <h2>{title}</h2>
        {actions ? <div className="panel-actions">{actions}</div> : null}
      </header>
      <div className="panel-body">{children}</div>
    </section>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      className="input"
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
    />
  );
}

export function NumberInput({
  value,
  onChange,
  min,
  max,
}: {
  value: number | '';
  onChange: (value: number | '') => void;
  min?: number;
  max?: number;
}) {
  return (
    <input
      className="input"
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(event) => {
        const raw = event.target.value;
        onChange(raw === '' ? '' : Number(raw));
      }}
    />
  );
}

export function Select<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <select className="input" value={value} onChange={(event) => onChange(event.target.value as T)}>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
}) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

/** Multi-select as checkboxes: the lists are short (11 organs, 14 diseases)
 *  and the whole point of these screens is seeing the set at a glance. */
export function SlugPicker({
  options,
  selected,
  onChange,
}: {
  options: { slug: string; name: string }[];
  selected: string[];
  onChange: (slugs: string[]) => void;
}) {
  const set = new Set(selected);

  return (
    <div className="slug-picker">
      {options.map((option) => (
        <label key={option.slug} className={set.has(option.slug) ? 'chip chip-on' : 'chip'}>
          <input
            type="checkbox"
            checked={set.has(option.slug)}
            onChange={(event) => {
              const next = new Set(set);
              if (event.target.checked) next.add(option.slug);
              else next.delete(option.slug);
              onChange([...next].sort());
            }}
          />
          {option.name}
        </label>
      ))}
    </div>
  );
}

export function Status({ error, message }: { error?: string | null; message?: string | null }) {
  if (error) return <p className="status status-error">{error}</p>;
  if (message) return <p className="status status-ok">{message}</p>;
  return null;
}

/** Renders the diff the server echoed back, so a save says what it changed. */
export function summariseChanges(changes: Changes | undefined | null): string {
  if (!changes || Object.keys(changes).length === 0) return 'Saved — nothing had changed.';

  const parts = Object.entries(changes).map(([field, { from, to }]) => {
    const format = (value: unknown) =>
      Array.isArray(value) ? `[${value.join(', ')}]` : value === null ? '—' : String(value);

    return `${field}: ${format(from)} → ${format(to)}`;
  });

  return `Saved. ${parts.join(' · ')}`;
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return <p className="muted">{label}</p>;
}
